import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'

// Runtime-log entry kinds — drives coloring and whether the status is
// shown as [OK] / [FAIL] / [...] / [DONE].
export type LogKind = 'info' | 'ok' | 'fail' | 'running' | 'done'

export interface LogEntry {
  id: number
  timestamp: number
  label: string // e.g. "fv.encoder.load"
  note?: string // e.g. "t5-xxl warm cache"
  kind: LogKind
}

// Status pips shown in the terminal header + collapsed bar. Drives the
// "activity lights" visual. Each one advances as we move through the
// generate pipeline.
export type Stage = 'compile' | 'safety' | 'route' | 'encode' | 'diffuse' | 'upload'
export type StageState = 'idle' | 'running' | 'ok' | 'fail'

export type StatusMap = Record<Stage, StageState>

const INITIAL_STATUS: StatusMap = {
  compile: 'idle',
  safety: 'idle',
  route: 'idle',
  encode: 'idle',
  diffuse: 'idle',
  upload: 'idle',
}

interface TerminalContextValue {
  // Dock state
  expanded: boolean
  setExpanded: (v: boolean) => void
  toggle: () => void

  // Live script preview state
  promptPreview: string
  setPromptPreview: (s: string) => void
  modelName: string
  setModelName: (s: string) => void

  // Runtime log
  logs: LogEntry[]
  pushLog: (entry: Omit<LogEntry, 'id' | 'timestamp'>) => void
  clearLogs: () => void

  // Stage pipeline
  status: StatusMap
  setStage: (stage: Stage, state: StageState) => void
  resetStatus: () => void

  // High-level helper — run the fake preflight sequence with staggered
  // delays so the runtime log animates in naturally before real inference
  // kicks off. All values here are codenames (sparrow / commons / etc.)
  // not real provider or model names — keeps the backend stack opaque
  // to anyone screenshotting the terminal.
  streamPreflight: (opts: {
    modelCode: string
    providerCode: string
    encoderCode: string
    tokens: number
  }) => Promise<void>
}

const Ctx = createContext<TerminalContextValue | null>(null)

const DOCK_STORAGE_KEY = 'fv_terminal_expanded'

function readDockState(): boolean {
  if (typeof window === 'undefined') return false
  const raw = window.localStorage.getItem(DOCK_STORAGE_KEY)
  // Default to expanded on first visit so the feature is discoverable.
  if (raw === null) return true
  return raw === '1'
}

export function TerminalProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpandedInner] = useState<boolean>(readDockState)
  const [promptPreview, setPromptPreview] = useState('')
  const [modelName, setModelName] = useState('flux-schnell')
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [status, setStatus] = useState<StatusMap>(INITIAL_STATUS)
  const idRef = useRef(0)

  const setExpanded = useCallback((v: boolean) => {
    setExpandedInner(v)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DOCK_STORAGE_KEY, v ? '1' : '0')
      // Expose the collapsed/expanded height as a CSS variable so the
      // PromptBar can offset itself above the terminal without prop drilling.
      document.documentElement.style.setProperty(
        '--terminal-height',
        v ? '320px' : '32px',
      )
    }
  }, [])

  const toggle = useCallback(() => setExpanded(!expanded), [expanded, setExpanded])

  // Kick off the CSS var once on mount so PromptBar has a sane offset
  // before any interaction.
  if (typeof window !== 'undefined') {
    const current = document.documentElement.style.getPropertyValue('--terminal-height')
    if (!current) {
      document.documentElement.style.setProperty(
        '--terminal-height',
        expanded ? '320px' : '32px',
      )
    }
  }

  const pushLog = useCallback(
    (entry: Omit<LogEntry, 'id' | 'timestamp'>) => {
      setLogs((prev) => [
        ...prev,
        {
          id: ++idRef.current,
          timestamp: Date.now(),
          ...entry,
        },
      ].slice(-60)) // hard-cap backlog to 60 lines
    },
    [],
  )

  const clearLogs = useCallback(() => {
    setLogs([])
  }, [])

  const setStage = useCallback((stage: Stage, state: StageState) => {
    setStatus((prev) => ({ ...prev, [stage]: state }))
  }, [])

  const resetStatus = useCallback(() => {
    setStatus(INITIAL_STATUS)
  }, [])

  const streamPreflight = useCallback(
    async ({
      modelCode,
      providerCode,
      encoderCode,
      tokens,
    }: {
      modelCode: string
      providerCode: string
      encoderCode: string
      tokens: number
    }) => {
      // Clear any prior run's log + reset the status pipeline
      setLogs([])
      setStatus(INITIAL_STATUS)

      const steps: Array<{
        stage: Stage | null
        delay: number
        label: string
        note: string
      }> = [
        { stage: 'compile', delay: 120, label: 'fv.compile()', note: `tokens=${tokens}` },
        { stage: 'safety', delay: 200, label: 'fv.safety.check(prompt)', note: 'nsfw=0.0 bias=0.0' },
        { stage: null, delay: 120, label: 'fv.credits.reserve(1)', note: 'ok' },
        {
          stage: 'route',
          delay: 180,
          label: 'fv.route.pick()',
          note: `lane=${providerCode} model=${modelCode}`,
        },
        {
          stage: 'encode',
          delay: 260,
          label: `fv.encoder.load("${encoderCode}")`,
          note: 'warm cache',
        },
        { stage: null, delay: 140, label: 'fv.gpu.acquire()', note: 'block=cell-7a' },
      ]

      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

      for (const step of steps) {
        if (step.stage) {
          setStatus((prev) => ({ ...prev, [step.stage!]: 'running' }))
        }
        await sleep(step.delay)
        setLogs((prev) => [
          ...prev,
          {
            id: ++idRef.current,
            timestamp: Date.now(),
            label: step.label,
            note: step.note,
            kind: 'ok' as LogKind,
          },
        ].slice(-60))
        if (step.stage) {
          setStatus((prev) => ({ ...prev, [step.stage!]: 'ok' }))
        }
      }
    },
    [],
  )

  const value: TerminalContextValue = {
    expanded,
    setExpanded,
    toggle,
    promptPreview,
    setPromptPreview,
    modelName,
    setModelName,
    logs,
    pushLog,
    clearLogs,
    status,
    setStage,
    resetStatus,
    streamPreflight,
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useTerminal(): TerminalContextValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useTerminal must be used inside TerminalProvider')
  return v
}
